// Single Context-Aware Phase Technology Resolver (Finding 2 & Finding 3)
//
// Centralizes forward and reverse mapping between granular `PhasePowerComponentKey`
// (plus `PhasePowerSourceConfig.fuelCellConfig`) and financial `TechnologyId`,
// enforcing the strict non-default rule: an unspecified fuel-cell chemistry or
// primary fuel NEVER silently defaults to `'fuel-cell-sofc'`.

import type {
  PhasePowerComponentKey,
  PhasePowerSourceConfig,
} from '../../types/siteRag';
import type { TechnologyId } from './types';
import { DEFAULT_PACKAGE_BY_TECHNOLOGY_ID } from './presets/stationaryFuelCell';

export type FuelCellTechnologyId =
  | 'fuel-cell-sofc'
  | 'fuel-cell-pafc'
  | 'fuel-cell-pem'
  | 'fuel-cell-mcfc';

export const FUEL_CELL_TECHNOLOGY_IDS: readonly FuelCellTechnologyId[] = [
  'fuel-cell-sofc',
  'fuel-cell-pafc',
  'fuel-cell-pem',
  'fuel-cell-mcfc',
] as const;

export function isFuelCellTechnologyId(
  tech: string | undefined | null,
): tech is FuelCellTechnologyId {
  return (
    tech === 'fuel-cell-sofc' ||
    tech === 'fuel-cell-pafc' ||
    tech === 'fuel-cell-pem' ||
    tech === 'fuel-cell-mcfc'
  );
}

export interface ResolvedPhaseTechnology {
  kind: 'single' | 'compare-all-suitable' | 'unresolved-configuration' | 'storage';
  technologyIds: readonly TechnologyId[];
  primaryTechnologyId: TechnologyId | null;
  unresolvedPosture?: 'fuel-cell-unspecified';
  displayLabel: string;
  shortLabel: string;
  capacityCreditPct: number;
  capacityFactorPct: number;
  landAcresPerNameplateMw: number;
}

/**
 * Resolve a granular `PhasePowerComponentKey` and optional `PhasePowerSourceConfig`
 * to its canonical technology posture (Finding 2 & Finding 3).
 *
 * CRITICAL INVARIANT (Negative Test 1):
 * When `component === 'fuel-cell'` and `fuelCellConfig` is absent or has
 * `chemistry === 'unspecified'` or `primaryFuel === 'unspecified'`, this
 * returns `kind: 'unresolved-configuration'`, `unresolvedPosture: 'fuel-cell-unspecified'`,
 * `primaryTechnologyId: null` — NEVER `'fuel-cell-sofc'`.
 */
export function resolvePhaseComponentTechnology(
  component: PhasePowerComponentKey,
  config?: PhasePowerSourceConfig,
): ResolvedPhaseTechnology {
  switch (component) {
    case 'recip':
      return {
        kind: 'single',
        technologyIds: ['gas-sc'],
        primaryTechnologyId: 'gas-sc',
        displayLabel: 'Reciprocating Engine',
        shortLabel: 'Recip Engine',
        capacityCreditPct: 92,
        capacityFactorPct: 15,
        landAcresPerNameplateMw: 0.4,
      };
    case 'gas-turbine':
      return {
        kind: 'single',
        technologyIds: ['gas-cc'],
        primaryTechnologyId: 'gas-cc',
        displayLabel: 'Gas Combined Cycle',
        shortLabel: 'Gas CC',
        capacityCreditPct: 92,
        capacityFactorPct: 85,
        landAcresPerNameplateMw: 0.4,
      };
    case 'fuel-cell': {
      const fc = config?.fuelCellConfig;
      if (
        !fc ||
        fc.chemistry === 'unspecified' ||
        fc.primaryFuel === 'unspecified'
      ) {
        return {
          kind: 'unresolved-configuration',
          technologyIds: [],
          primaryTechnologyId: null,
          unresolvedPosture: 'fuel-cell-unspecified',
          displayLabel: 'Stationary Fuel Cell (Unresolved Chemistry/Fuel)',
          shortLabel: 'Fuel Cell (Unresolved)',
          capacityCreditPct: 0,
          capacityFactorPct: 0,
          landAcresPerNameplateMw: 0,
        };
      }

      if (fc.chemistry === 'compare-all-suitable') {
        const candidates: readonly FuelCellTechnologyId[] =
          fc.primaryFuel === 'hydrogen'
            ? ['fuel-cell-pem', 'fuel-cell-sofc']
            : ['fuel-cell-sofc', 'fuel-cell-pafc', 'fuel-cell-pem', 'fuel-cell-mcfc'];
        return {
          kind: 'compare-all-suitable',
          technologyIds: candidates,
          primaryTechnologyId: null,
          displayLabel: 'Stationary Fuel Cells (Compare All Suitable)',
          shortLabel: 'Fuel Cells (Compare)',
          capacityCreditPct: 94,
          capacityFactorPct: 94,
          landAcresPerNameplateMw: 0.25,
        };
      }

      const techMap: Record<'sofc' | 'pafc' | 'pem' | 'mcfc', FuelCellTechnologyId> = {
        sofc: 'fuel-cell-sofc',
        pafc: 'fuel-cell-pafc',
        pem: 'fuel-cell-pem',
        mcfc: 'fuel-cell-mcfc',
      };
      const techId = techMap[fc.chemistry];
      const pkg = DEFAULT_PACKAGE_BY_TECHNOLOGY_ID[techId];
      return {
        kind: 'single',
        technologyIds: [techId],
        primaryTechnologyId: techId,
        displayLabel: pkg.label,
        shortLabel:
          techId === 'fuel-cell-sofc'
            ? 'Fuel Cell (SOFC)'
            : techId === 'fuel-cell-pafc'
              ? 'Fuel Cell (PAFC)'
              : techId === 'fuel-cell-pem'
                ? 'Fuel Cell (PEMFC)'
                : 'Fuel Cell (MCFC)',
        capacityCreditPct: pkg.benchmarks.capacityCreditPct,
        capacityFactorPct: pkg.benchmarks.capacityFactorPct,
        landAcresPerNameplateMw: pkg.benchmarks.landAcresPerNameplateMw,
      };
    }
    case 'battery':
      return {
        kind: 'storage',
        technologyIds: [],
        primaryTechnologyId: null,
        displayLabel: 'Battery (BESS)',
        shortLabel: 'BESS',
        capacityCreditPct: 95,
        capacityFactorPct: 95,
        landAcresPerNameplateMw: 0,
      };
    case 'grid':
      return {
        kind: 'single',
        technologyIds: ['grid-tariff'],
        primaryTechnologyId: 'grid-tariff',
        displayLabel: 'Grid import',
        shortLabel: 'Grid',
        capacityCreditPct: 97,
        capacityFactorPct: 99,
        landAcresPerNameplateMw: 0,
      };
    case 'solar-bess':
      return {
        kind: 'single',
        technologyIds: ['solar-bess'],
        primaryTechnologyId: 'solar-bess',
        displayLabel: 'Solar+BESS',
        shortLabel: 'Solar+BESS',
        capacityCreditPct: 30,
        capacityFactorPct: 28,
        landAcresPerNameplateMw: 5,
      };
    case 'wind-bess':
      return {
        kind: 'single',
        technologyIds: ['wind-bess'],
        primaryTechnologyId: 'wind-bess',
        displayLabel: 'Wind+BESS',
        shortLabel: 'Wind+BESS',
        capacityCreditPct: 30,
        capacityFactorPct: 38,
        landAcresPerNameplateMw: 0.75,
      };
    case 'smr':
      return {
        kind: 'single',
        technologyIds: ['nuclear-smr'],
        primaryTechnologyId: 'nuclear-smr',
        displayLabel: 'SMR',
        shortLabel: 'SMR',
        capacityCreditPct: 93,
        capacityFactorPct: 93,
        landAcresPerNameplateMw: 1.0,
      };
    case 'large-nuclear':
      return {
        kind: 'single',
        technologyIds: ['nuclear-large'],
        primaryTechnologyId: 'nuclear-large',
        displayLabel: 'Large Nuclear',
        shortLabel: 'Large Nuclear',
        capacityCreditPct: 93,
        capacityFactorPct: 93,
        landAcresPerNameplateMw: 0.9,
      };
    default: {
      const _never: never = component;
      return _never;
    }
  }
}

/**
 * Forward bridge from granular `PhasePowerComponentKey` (+ optional `PhasePowerSourceConfig`)
 * to `TechnologyId | null`.
 */
export function phaseComponentToTechnologyId(
  component: PhasePowerComponentKey,
  config?: PhasePowerSourceConfig,
): TechnologyId | null {
  return resolvePhaseComponentTechnology(component, config).primaryTechnologyId;
}

/**
 * Reverse bridge from finance `TechnologyId` to granular `PhasePowerComponentKey | null`.
 */
export function technologyIdToPhaseComponent(
  tech: TechnologyId,
): PhasePowerComponentKey | null {
  switch (tech) {
    case 'gas-sc':
      return 'recip';
    case 'gas-cc':
      return 'gas-turbine';
    case 'fuel-cell-sofc':
    case 'fuel-cell-pafc':
    case 'fuel-cell-pem':
    case 'fuel-cell-mcfc':
      return 'fuel-cell';
    case 'grid-tariff':
      return 'grid';
    case 'solar-bess':
      return 'solar-bess';
    case 'wind-bess':
      return 'wind-bess';
    case 'nuclear-smr':
      return 'smr';
    case 'nuclear-large':
      return 'large-nuclear';
    case 'mixed':
    default:
      return null;
  }
}
